<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <div class="pt-6 pb-10">
    <ContentCard class="mb-8">
      <template #header>
        <ContentCardSubheader :back-to="{ name: ReviewsRouteName.ReviewsProductReviewsAndCampaigns, params: { productId: product.id } }">
          <span class="line-clamp-1">{{ t('Reviews.product.view.campaignDetailView.title', { campaignName: campaign?.title }) }}</span>
          <template #actions>
            <div class="flex items-center gap-4">
              <button
                v-if="showPauseCampaignButton"
                class="btn-secondary-color-xs p-2 whitespace-nowrap"
                data-testid="pause-campaign-button"
                :disabled="updateCampaignLoading"
                @click="toggleCampaign()"
              >
                {{ t('Reviews.product.campaign.action.pauseCampaign') }}
                <CirclePauseIcon class="lucide-icon-s" />
              </button>
              <RouterLink
                v-if="campaign"
                class="btn-secondary-color-xs p-2 whitespace-nowrap"
                data-testid="reviews-campaign-edit-button"
                :to="{
                  name: ReviewsRouteName.ReviewsProductCampaignEdit,
                  params: { productId: product.id, campaignId: campaignId },
                }"
                @click="
                  track('campaign_update_start', { surface: 'editor', entity_type: 'tool', entity_id: product.id, campaign_id: campaignId })
                "
              >
                {{ t('Reviews.product.campaign.action.editCampaign') }}<PencilIcon class="lucide-icon-s" />
              </RouterLink>
            </div>
          </template>
        </ContentCardSubheader>
      </template>

      <div v-if="campaignStatus === 'inactive'" class="relative -mx-6 border-b border-main">
        <MessageBanner
          type="info"
          solid
          :headline="t('Reviews.product.view.campaignDetailView.inactiveCampaignInfoHeadline')"
          :message="t('Reviews.product.view.campaignDetailView.inactiveCampaignInfoMessage')"
          icon="mdi-information-outline"
          class="rounded-none"
        >
        </MessageBanner>
      </div>
      <div v-else-if="campaign && !campaign?.enabled" class="relative -mx-6 border-b border-main">
        <MessageBanner
          type="warning"
          solid
          :headline="t('Reviews.product.view.campaignDetailView.disabledCampaignInfoHeadline')"
          :message="t('Reviews.product.view.campaignDetailView.disabledCampaignInfoMessage')"
          icon="mdi-information-outline"
          class="rounded-none"
        >
          <template #action>
            <button class="btn-outline-neutral-xs p-2" data-testid="reviews-campaign-reactivate" @click="toggleCampaign()">
              {{ t('Reviews.product.campaign.action.reactivateCampaign') }}
              <CirclePlayIcon class="lucide-icon-xs" />
            </button>
          </template>
        </MessageBanner>
      </div>

      <div class="p-0 py-6">
        <div v-if="loading || !campaign || updateCampaignLoading" class="mt-6 flex justify-center">
          <CircleSpinner />
        </div>
        <div v-else class="grid grid-cols-3 gap-4">
          <div class="rounded-sm bg-surface-elevation-1 p-4 pb-0 shadow-sm">
            <div class="mb-4 flex items-center justify-between">
              <div class="text-headline-xs text-subtle">
                {{ t('Reviews.product.view.campaignDetailView.campaignBoxTitle') }}
              </div>
              <div class="flex items-center gap-1">
                <CampaignStatusBadge v-if="campaignStatus" :status="campaignStatus" />
              </div>
            </div>
            <div class="text-label-m text-subtlest">
              {{ t('Reviews.product.view.campaignDetailView.detailsBoxCampaignStart') }}
            </div>
            <div class="mb-2 text-body-s-bold text-subtle">
              {{ campaign?.validFrom ? new Date(campaign.validFrom).toLocaleDateString() : '-' }}
            </div>

            <div class="text-label-m text-subtlest">
              {{ t('Reviews.product.view.campaignDetailView.detailsBoxCampaignEnd') }}
            </div>
            <div class="mb-2 text-body-s-bold text-subtle">
              {{ campaign?.validTo ? new Date(campaign.validTo).toLocaleDateString() : '-' }}
            </div>
          </div>
          <div class="rounded-sm bg-surface-elevation-1 p-4 pb-0 shadow-sm">
            <div class="mb-8 flex items-center justify-between">
              <div class="text-headline-xs text-subtle">
                {{ t('Reviews.product.view.campaignDetailView.reviewsBoxTitle') }}
              </div>
              <div v-if="incentivizationProgress >= 100">
                <ChipBadge color="default" rounded>{{ t('Reviews.product.campaign.reviewsLimit.reached') }}</ChipBadge>
              </div>
            </div>
            <div class="text-label-m text-subtlest">
              {{ t('Reviews.product.view.campaignDetailView.detailsBoxReviewsReceived') }}
            </div>
            <div class="mb-4 text-body-l-bold text-subtle" data-testid="campaign-reviews-received-count">
              {{ campaign.associatedReviewsCount ?? 0 }}
            </div>
            <div class="flex justify-between">
              <div>
                <div class="text-label-m text-subtlest">
                  <span
                    v-if="hasIncentivizationsLimit"
                    class="inline-block h-2 w-2 rounded-full"
                    :class="incentivizationProgress >= 100 ? 'bg-success-strong' : 'bg-primary-strong'"
                  ></span>
                  {{ t('Reviews.product.view.campaignDetailView.detailsBoxIncentivesReceived') }}
                </div>
                <div>
                  <span class="mb-2 text-body-l-bold text-subtle">{{ campaign.wonIncentivizationsCount || 0 }}</span>
                  <span v-if="campaign.incentivizationsLimit" class="text-subtlest"> / {{ campaign.incentivizationsLimit }} </span>
                  <span v-if="campaign.limitType === 'budget' && campaign.incentivizationsLimit" class="text-label-m text-subtlest">
                    ({{ incentivizationProgress }}%)
                  </span>
                </div>
              </div>
              <div :class="campaign?.limitType === 'incentivizations' ? '-mt-16' : ''">
                <div class="text-label-m text-subtlest">
                  {{ t('Reviews.product.view.campaignDetailView.detailsBoxIncentiveType') }}
                </div>
                <ChipBadge color="default" rounded class="mt-1 capitalize">
                  {{ campaign.incentivizationType }} {{ incentiveAmount }}
                </ChipBadge>
              </div>
            </div>
            <LinearProgress
              v-if="campaign.limitType === 'incentivizations' && campaign.incentivizationsLimit"
              class="mt-2 mb-4"
              percentage="outside"
              :progress="incentivizationProgress"
              overshoot-style="success"
            />
          </div>
          <BudgetBox
            is-detail-budget
            :has-reviews-limit="!!hasIncentivizationsLimit"
            :title="t('Reviews.product.view.campaignDetailView.budgetBoxTitle')"
            class="w-full"
            :used-budget="usedBudget"
            :total-budget="totalBudget"
            :global-budget="product.currentBudget?.remainingValue ?? 0"
          />
        </div>
      </div>
    </ContentCard>
    <ReviewsProductCampaignLinks v-if="campaign" :campaign="campaign" :product-id="product.id" :product-slug="product.slug" />
    <ReviewsProductReviews :product="product" :campaign-id="campaignId" is-detail-view />
  </div>
</template>

<script setup lang="ts">
import { ChipBadge, CircleSpinner, useCurrencyFormatter, LinearProgress, MessageBanner } from '@ramp106/omrjs-core-ui'
import { CirclePauseIcon, CirclePlayIcon, PencilIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { isNumber } from 'lodash-es'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import { useReviewsProductBreadcrumbs } from '@/composables/breadcrumbs/useReviewsProductBreadcrumbs'
import type { ProductData } from '@/gql/reviews'
import { useReviewsIncentivizationCampaignQuery, useUpdateReviewsCampaignMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

import BudgetBox from './BudgetBox.vue'
import CampaignStatusBadge from './CampaignStatusBadge.vue'
import ReviewsProductCampaignLinks from './ReviewsProductCampaignLinks.vue'
import ReviewsProductReviews from './ReviewsProductReviews.vue'

const props = defineProps<{
  product: ProductData
}>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { formatCurrency } = useCurrencyFormatter()

const campaignId = route.params.campaignId as string

const { result, loading, refetch } = useReviewsIncentivizationCampaignQuery(
  computed(() => ({ campaignId })),
  { clientId: 'reviews' },
)

const campaign = computed(() => result.value?.manage?.incentivizationCampaign)

const campaignStatus = computed(() => campaign.value?.status)

watch([loading, campaign], ([isLoading, campaignData]) => {
  if (!isLoading && !campaignData) {
    router.push({ name: '404' })
  }
})

const showPauseCampaignButton = computed(() => {
  if (!campaign.value || !campaign.value.enabled) return false

  const now = new Date(new Date().toISOString().split('T')[0])
  const startDate = campaign.value.validFrom ? new Date(campaign.value.validFrom) : null
  const endDate = campaign.value.validTo ? new Date(campaign.value.validTo) : null

  return startDate !== null && now >= startDate && (endDate === null || now <= endDate)
})

const incentiveAmount = computed(() => formatCurrency(campaign.value?.amount || 0))

const totalBudget = computed<number | null>(() => {
  if (!isNumber(campaign.value?.incentivizationsLimit) || !isNumber(campaign.value?.amount)) {
    return null
  }
  return campaign.value?.incentivizationsLimit * campaign.value?.amount
})

const usedBudget = computed(() => {
  if (!isNumber(campaign.value?.incentivizationsLimit) || !isNumber(campaign.value?.amount)) {
    return 0
  }
  return (campaign.value?.wonIncentivizationsCount || 0) * campaign.value?.amount
})

const incentivizationProgress = computed(() => {
  if (isNumber(campaign.value?.incentivizationsLimit) && isNumber(campaign.value?.wonIncentivizationsCount)) {
    return Math.round((campaign.value?.wonIncentivizationsCount / campaign.value?.incentivizationsLimit) * 100)
  }
  return 0
})

const hasIncentivizationsLimit = computed(() => campaign.value?.limitType === 'incentivizations' && campaign.value?.incentivizationsLimit)

const { mutate: mutateUpdateCampaign, loading: updateCampaignLoading } = useUpdateReviewsCampaignMutation({
  clientId: 'reviews',
  refetchQueries: ['reviewsIncentivizationCampaign'],
})
const toggleCampaign = async () => {
  if (!campaign.value?.id) return
  try {
    await mutateUpdateCampaign({
      campaign: campaign.value.id,
      attributes: { enabled: !campaign.value.enabled },
    })
    await refetch()
    showNotification(
      t(campaign.value.enabled ? 'Reviews.product.notifications.campaignEnabled' : 'Reviews.product.notifications.campaignDisabled'),
      'success',
    )
  } catch {
    showNotification(t('Reviews.product.notifications.campaignToggleActiveError'), 'error')
  }
}

const campaignTitle = computed(() => campaign.value?.title || t('Reviews.product.view.campaignDetailView.loading'))
const breadcrumbs = useReviewsProductBreadcrumbs(props.product, campaignTitle)
</script>
